/**
 * 58. Adjustments — adjustment request; reason; approval; posting.
 *
 * Arabic and English (C7): every word from the catalogs; the adjustment
 * number is a placeholder in the explanation, never typed into it; statuses
 * agree with the adjustment.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
  BlockedActionExplanation,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatQuantity, formatDate } from "@/lib/money";

interface AdjustmentRow {
  id: string;
  adjustmentNumber: string;
  productSku: string;
  productName: string;
  warehouseName: string;
  quantityDelta: string;
  reason: string;
  requestedBy: string;
  requestedAt: string;
  status: "Draft" | "PendingApproval" | "Approved" | "Posted" | "Rejected";
}

export function AdjustmentsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<AdjustmentRow>(
    ["inventory", "adjustments"],
    "/inventory/movements?movement_type=Adjustment",
  );

  const pending = (data?.items ?? []).filter(
    (r) => r.status === "PendingApproval",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.adjustments.title")}
        subtitle={t("inventory.adjustments.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="stockMovement"
            slice="MF-B1b"
          >
            {t("inventory.adjustments.new")}
          </PendingButton>
        }
      />
      {pending.length > 0 && (
        <BlockedActionExplanation
          title={t("inventory.adjustments.awaiting")}
          reasons={pending.map((r) => ({
            code: "APPROVAL_REQUIRED",
            message: t("inventory.adjustments.exceeds", {
              number: r.adjustmentNumber,
            }),
            resolutionPath: t("inventory.adjustments.review"),
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.adjustments.list"), data.total)}>
          <DataTable<AdjustmentRow>
            rows={data.items}
            columns={[
              {
                key: "adjustmentNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.adjustmentNumber}</span>
                ),
              },
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "quantityDelta",
                label: t("inventory.adjustments.delta"),
                numeric: true,
                render: (r) => (
                  <Badge
                    tone={Number(r.quantityDelta) >= 0 ? "success" : "danger"}
                  >
                    {formatQuantity(r.quantityDelta)}
                  </Badge>
                ),
              },
              { key: "reason", label: t("fields.reason") },
              { key: "requestedBy", label: t("fields.requestedBy") },
              {
                key: "requestedAt",
                label: t("fields.requested"),
                render: (r) => formatDate(r.requestedAt),
              },
              {
                key: "status",
                label: t("fields.status"),
                // PendingApproval keeps the "Pending" colour it had.
                render: (r) =>
                  r.status === "PendingApproval" ? (
                    <Badge tone="warning">
                      {statusLabel(t, "StockAdjustment", r.status)}
                    </Badge>
                  ) : (
                    <StatusBadge status={r.status} type="StockAdjustment" />
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
